import { useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, Stack, CircularProgress, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTranslation } from 'react-i18next';
import {
  useAttachments, useUploadAttachment, useDeleteAttachment, downloadAttachment,
  type AttachmentEntityType, type Attachment,
} from '../../api/hooks/useAttachments';
import { useNotifications } from '../../context/NotificationContext';
import { formatApiError } from '../../api/client';

function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Datei-Anhaenge fuer eine Entitaet (Meeting / offener Punkt). Upload, Liste,
 * Download, Loeschen. Solange keine entityId existiert (Datensatz noch nicht
 * gespeichert), wird ein Hinweis statt der Upload-Aktion gezeigt.
 */
export function AttachmentList({
  projectId, entityType, entityId, compact,
}: {
  projectId: string;
  entityType: AttachmentEntityType;
  entityId: string | undefined;
  compact?: boolean;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useAttachments(projectId, entityType, entityId);
  const items = ((data as unknown as { data?: Attachment[] } | undefined)?.data ?? []);
  const uploadMut = useUploadAttachment(projectId, entityType, entityId ?? '');
  const deleteMut = useDeleteAttachment(projectId, entityType, entityId ?? '');

  if (!entityId) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
        {t('attachments.saveFirst', 'Erst speichern, dann lassen sich Dateien anhängen.')}
      </Typography>
    );
  }

  const onPick = () => inputRef.current?.click();
  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        await uploadMut.mutateAsync(f);
      }
      notify(t('attachments.uploaded', 'Datei angehängt'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDelete = async (id: string) => {
    try { await deleteMut.mutateAsync(id); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(formatApiError(err), 'error'); }
  };

  const onDownload = async (att: Attachment) => {
    try { await downloadAttachment(projectId, att); }
    catch (err) { notify(formatApiError(err), 'error'); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant={compact ? 'caption' : 'subtitle2'} sx={{ color: 'var(--pm-fg-muted)', fontWeight: 600 }}>
          {t('attachments.title', 'Anhänge')}{items.length > 0 ? ` (${items.length})` : ''}
        </Typography>
        <Button
          size="small"
          startIcon={busy ? <CircularProgress size={14} /> : <AttachFileIcon fontSize="small" />}
          onClick={onPick}
          disabled={busy}
          sx={{ color: 'var(--pm-fg-muted)' }}
        >
          {t('attachments.add', 'Datei anhängen')}
        </Button>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => void onFiles(e.target.files)} />
      </Stack>

      {isLoading ? (
        <CircularProgress size={18} />
      ) : items.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
          {t('attachments.empty', 'Noch keine Anhänge.')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {items.map((att) => (
            <Stack
              key={att.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'var(--pm-bg-subtle)' }}
            >
              <InsertDriveFileIcon fontSize="small" sx={{ color: 'var(--pm-fg-subtle)' }} />
              <Typography variant="body2" sx={{ flex: 1, color: 'var(--pm-fg-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {att.filename}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>{formatSize(att.size)}</Typography>
              <Tooltip title={tc('actions.download', 'Herunterladen')} arrow>
                <IconButton size="small" onClick={() => void onDownload(att)} sx={{ color: 'var(--pm-fg-muted)' }}><DownloadIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title={tc('actions.delete', 'Löschen')} arrow>
                <IconButton size="small" onClick={() => void onDelete(att.id)} sx={{ color: 'var(--pm-error)' }}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
